/**
 * Catching the address someone meant to type.
 *
 * A wrong address is worse here than almost anywhere else: the photo link, the
 * ticket and the start time all go to it, nothing bounces back to the person
 * who typed it, and they find out on race morning. "gamil.comm" costs an
 * athlete their photos and costs the organiser a support message.
 *
 * This suggests, it never rejects. Real addresses live on domains no list of
 * ours will ever contain, so anything that is structurally valid must be
 * allowed through even when it looks odd.
 */

/** Structural check only: there is an @, something either side, and a dotted domain. */
export function isStructurallyValidEmail(value: string): boolean {
  const email = value.trim();
  if (!email || email.length > 254) return false;
  if (/\s/.test(email)) return false;

  const at = email.lastIndexOf('@');
  if (at <= 0 || at === email.length - 1) return false;

  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (local.length > 64) return false;
  if (email.split('@').length !== 2) return false;

  // A domain needs at least one dot, labels either side, and a TLD of letters.
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(domain)) return false;
  const tld = domain.slice(domain.lastIndexOf('.') + 1);
  if (!/^[a-z]{2,}$/i.test(tld)) return false;

  // Local part: ordinary characters, no leading, trailing or doubled dots.
  if (!/^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/i.test(local)) return false;

  return true;
}

/** The domains almost every athlete actually uses. */
const COMMON_DOMAINS = [
  'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.co.uk', 'outlook.com',
  'outlook.ie', 'live.com', 'live.co.uk', 'yahoo.com', 'yahoo.co.uk', 'yahoo.ie',
  'icloud.com', 'me.com', 'mac.com', 'aol.com', 'protonmail.com', 'proton.me',
  'eircom.net', 'gmx.com', 'msn.com', 'btinternet.com', 'sky.com', 'virginmedia.com',
];

/** Endings people type when they mean a real one. */
const TLD_FIXES: Record<string, string> = {
  con: 'com', cim: 'com', clm: 'com', comm: 'com', cpm: 'com', vom: 'com',
  co: 'com', cm: 'com', om: 'com', ocm: 'com', xom: 'com', cok: 'com',
  nte: 'net', ner: 'net', ent: 'net', og: 'org', ogr: 'org', orh: 'org',
  'co.ukk': 'co.uk', 'co.uj': 'co.uk', 'co.iw': 'co.uk',
};

/** Levenshtein distance, capped: anything past the cap is not a typo. */
function distance(a: string, b: string, cap: number): number {
  if (Math.abs(a.length - b.length) > cap) return cap + 1;

  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    let rowBest = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const value = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost);
      current.push(value);
      if (value < rowBest) rowBest = value;
    }
    // Every remaining row can only get worse, so bail out early.
    if (rowBest > cap) return cap + 1;
    previous = current;
  }
  return previous[b.length];
}

/**
 * The address they probably meant, or null when this one looks fine.
 *
 * Only ever suggests a domain: the part before the @ is a name we have no
 * business second-guessing.
 */
export function suggestEmail(value: string): string | null {
  const email = value.trim().toLowerCase();
  const at = email.lastIndexOf('@');
  if (at <= 0 || at === email.length - 1) return null;

  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (!domain.includes('.')) return null;
  if (COMMON_DOMAINS.includes(domain)) return null;

  // A mistyped ending on an otherwise fine domain: gamil.comm, mydomain.con.
  const lastDot = domain.lastIndexOf('.');
  const stem = domain.slice(0, lastDot);
  const tld = domain.slice(lastDot + 1);
  const fixedTld = TLD_FIXES[tld];
  if (fixedTld) {
    const repaired = `${stem}.${fixedTld}`;
    // Fix the stem too if it is also close to something familiar.
    const closerStem = COMMON_DOMAINS.find((d) => d === repaired) ? repaired : nearestDomain(repaired);
    return `${local}@${closerStem || repaired}`;
  }

  const nearest = nearestDomain(domain);
  return nearest ? `${local}@${nearest}` : null;
}

/**
 * The common domain within one or two characters, or null.
 *
 * Two edits is the useful limit. Three starts matching domains that merely
 * rhyme, and suggesting "gmail.com" to someone on a small company domain is
 * how a correct address gets talked out of them.
 */
function nearestDomain(domain: string): string | null {
  let best: string | null = null;
  let bestDistance = 3;

  for (const candidate of COMMON_DOMAINS) {
    // A short domain needs a tighter limit: two edits from "sky.com" reaches
    // plenty of real addresses.
    const cap = candidate.length <= 8 ? 1 : 2;
    const d = distance(domain, candidate, Math.min(cap, bestDistance - 1));
    if (d <= cap && d < bestDistance) {
      best = candidate;
      bestDistance = d;
    }
  }
  return best;
}
